import { ColDef } from "ag-grid-community";
export interface ColumnToAggregate {
    colDef: ColDef;
    formatter?: (value: string) => string;
    aggregateFunction?: "sum" | "avg";
}

interface GridStatsProps<T extends object> {
    selectedRows: T[]; // These are AG Grid rows
    columnsToAggregate?: ColumnToAggregate[];
}

const GridStats = <T extends object>({ selectedRows, columnsToAggregate }: GridStatsProps<T>) => {
    const calculateStats = (column: ColumnToAggregate) => {
        const sumAndCount = selectedRows.reduce(
            (acc, row: T) => {
                const field = column.colDef.field as keyof T;
                if (field && field in row) {
                    const value = Number(row[field]);
                    if (!isNaN(value)) {
                        return {
                            sum: acc.sum + value,
                            count: acc.count + 1,
                        };
                    }
                }
                return acc;
            },
            { sum: 0, count: 0 },
        );

        const aggregateFunc = column.aggregateFunction || "sum";
        const value = aggregateFunc === "sum" ? sumAndCount.sum : sumAndCount.count > 0 ? sumAndCount.sum / sumAndCount.count : 0;
        const formatter = column.formatter || ((value) => value);

        return {
            value: formatter(value.toString()),
            label: column.colDef.headerName,
            prefix: aggregateFunc === "avg" ? "Avg" : "Total",
        };
    };

    if (!columnsToAggregate || columnsToAggregate.length === 0) {
        return (
            <div className="bg-gray-50 border rounded-md p-4 mb-4">
                <div className="text-sm font-medium text-gray-500">
                    Selected: <span className="font-semibold text-gray-900">{selectedRows.length} rows</span>
                </div>
            </div>
        );
    }

    const stats = columnsToAggregate.map((column) => calculateStats(column));
    const totalStats = stats.filter((stat) => stat.prefix === "Total");
    const avgStats = stats.filter((stat) => stat.prefix === "Avg");

    // Calculate number of columns to display (max 4 per row)
    const columnsPerRow = Math.min(4, Math.max(totalStats.length, avgStats.length));

    return (
        <div className="hidden sm:block bg-gray-50 border rounded-md p-4 mb-4 w-full">
            <div className="mb-3 text-sm font-medium text-gray-500">
                Selected: <span className="font-semibold text-gray-900">{selectedRows.length} rows</span>
            </div>

            <div
                className="grid gap-4"
                style={{
                    gridTemplateColumns: `repeat(${columnsPerRow}, minmax(200px, 1fr))`,
                }}
            >
                {totalStats.map((stat, index) => (
                    <div key={`total-${index}`} className="flex justify-between items-center bg-white p-2 rounded border">
                        <span className="text-sm text-gray-600">
                            {stat.prefix} {stat.label}:
                        </span>
                        <span className="text-sm font-semibold text-gray-900">{stat.value}</span>
                    </div>
                ))}
            </div>

            {avgStats.length > 0 && (
                <div
                    className="grid gap-4 mt-4"
                    style={{
                        gridTemplateColumns: `repeat(${columnsPerRow}, minmax(200px, 1fr))`,
                    }}
                >
                    {avgStats.map((stat, index) => (
                        <div key={`avg-${index}`} className="flex justify-between items-center bg-white p-2 rounded border">
                            <span className="text-sm text-gray-600">
                                {stat.prefix} {stat.label}:
                            </span>
                            <span className="text-sm font-semibold text-gray-900">{stat.value}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default GridStats;
